/**
 * ONE ASK FROM HOME, AND THE OFFICE'S DECISION ON IT (ops #19, step 7; `docs/seams/home.md` section
 * 4). A client asked for something on her household's own page, `home.faithfullykept.com`: a skip, a
 * move, something for the cleaner, a change to the notes on her home, a person added, or she wrote.
 * The office decides it exactly as it would decide a phone call, and this is where.
 *
 * HER WORDS FIRST, AS SHE TYPED THEM, and nothing rewritten: what she asked, about which clean or home,
 * the days that suit her, what she proposes against what the home says now. Then the decision: an
 * answer she reads on her page, Accept and Decline, and by kind what accepting needs: a skip's charge
 * and the confirmation she will read, a move's day and the message that tells her, the person she
 * asked to add once the office has added her to the household. An add-on cannot be accepted yet,
 * since ops has no add-on door: the office rings her and declines with a word.
 *
 * AND THE ASKS BEHIND HER SETTINGS (ops #76 item 5, section 12.4), each the office's to decide. A pause,
 * a resume, a change or an ending of her standing cleans is read against what the home has now, with
 * the days, the cadence and the hours of the door the office uses for the same thing asked on the
 * phone, each starting from what she asked; the reason every clean taken off carries is typed, never
 * chosen for her, and the screen says before the press when the decision is not what she asked and so
 * needs a call. A change is news to her, so the words she reads are drafted from the decision's own
 * figures, her price of one clean read off the card or the agreement, for the office to approve or
 * edit. A person she asked to remove, change or take off home is named from the household, and her own
 * new details are shown beside the ones on file.
 *
 * WHAT IS DRAWN IS UX AND NEVER THE CONTROL. The office's route checks her door again, refuses an
 * answer that reads like a code, holds the reason honest, and moves the calendar last; what this sends
 * is the decision and the words, and the server's sentence is shown as it comes back.
 *
 * A SKIP'S CHARGE IS LEFT TO BE DECIDED UNLESS SOMEBODY DECIDES IT, as the move flow's own cancel
 * leaves it: nothing chosen sends nothing, and the change is written `not_decided`, which the visit's
 * page asks about again. An Accept nobody looked at is never a waiver of a late-notice charge.
 *
 * AND WHAT DID NOT GO IS SAID. The decision answers whether the message it wrote will go: refused by
 * her consent or held by the go-live switch, nothing is sent, and a move then waits until somebody
 * tells her and says so on the clean. So the answer is read, and the screen says nothing was sent and
 * to ring her, with the clean one tap away, before it says anything else; and before Accept it says
 * what the register would refuse, as the move flow does.
 *
 * AND WHERE OPS'S OWN SWITCH WOULD HOLD IT (ops #76 O6): on the channel chosen, the line the move flow
 * draws, texting's (`TextingGate`) or client email's (`EmailGate`), read off `GET /api/me` and her
 * number or address as the household holds them, naming the switch where the words would not go. It
 * warns as the register's line does and takes nothing away: accepted anyway, a move waits on the clean
 * until the office says she was told by phone, and the screen then says so; the server asks the switch
 * again either way.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CADENCES,
  CADENCE_LABELS,
  CHARGE_DECISIONS,
  CHARGE_DECISION_LABELS,
  SILENCE_REASON_LABELS,
  heldByEmailSwitch,
  humanise,
  type Cadence,
  type ChargeDecision,
  type RateLine,
  type SilenceReason,
} from '@fk/shared';
import {
  ApiError,
  decideHomeRequest,
  getCatalog,
  getClient,
  getHomeRequest,
  listAgreements,
  listMemberships,
  previewMessage,
  type AgreementList,
  type ClientRecord,
  type ContactMessaging,
  type ContactRow,
  type HomeDecided,
  type HomeDecision,
  type HomeRequestView,
  type Me,
  type MembershipRow,
} from '../api.ts';
import {
  centsFromDollars,
  dayOf,
  formatDay,
  formatInstant,
  formatPhone,
  formatWindow,
  isDay,
  isTimeOfDay,
  longDay,
} from '../format.ts';
import {
  PEOPLE_ASK_KINDS,
  SCHEDULE_ASK_KINDS,
  askedInWords,
  changeAsAsked,
  changeAsked,
  draftChange,
  draftConfirmation,
  firstNameOf,
  firstOnOrAfter,
  firstUnwritten,
  payloadText,
  serviceOfAsk,
  standingInWords,
  standingOf,
  weekdayWordOf,
  wordsOnChannel,
  type Standing,
} from '../home-asks.ts';
import {
  HOME_REQUEST_CANCEL_REASON_LABELS,
  HOME_REQUEST_KIND_LABELS,
  HOME_REQUEST_STATE_LABELS,
} from '../labels.ts';
import { choiceLineOf, choiceWords } from '../choices.ts';
import { Detail, Pill } from './AppShell.tsx';
import { EmailGate, emailPath } from './EmailGate.tsx';
import { Button, Field, SelectField, TextAreaField, TextField } from './Field.tsx';
import { Busy, ErrorNotice } from './Notice.tsx';
import { houseLineForHome } from './PublishedRates.tsx';
import { TextingGate, textingPath } from './TextingGate.tsx';

const STATE_TONE = {
  received: 'gold',
  accepted: 'sage',
  declined: 'quiet',
  cancelled: 'quiet',
} as const;

type Channel = 'sms' | 'email';
const CHANNELS: readonly Channel[] = ['sms', 'email'];
const CHANNEL_WORDS: Record<Channel, string> = { sms: 'A text', email: 'An email' };

/** Ops's two go-live switches, as `GET /api/me` tells the office of them. */
export type Switches = Pick<Me, 'smsGoLive' | 'emailGoLive'>;

/** Where ops's own switch would hold the words she reads: the one line, on the channel chosen. */
function SwitchLine({
  channel,
  me,
  her,
  wayOn,
}: {
  channel: Channel;
  me: Switches;
  her: Pick<ContactRow, 'phoneE164' | 'email'> | null | undefined;
  wayOn: string;
}) {
  return channel === 'email' ? (
    <EmailGate path={emailPath(me.emailGoLive, her?.email ?? null)} wayOn={wayOn} />
  ) : (
    <TextingGate path={textingPath(me.smsGoLive, her?.phoneE164 ?? null)} wayOn={wayOn} />
  );
}

/**
 * THE REASONS AN ASK OF HERS CAN CARRY (section 12.4): she asked for it herself, which the door admits
 * only when the decision is exactly what she asked, or the office told her on a call or in person,
 * which admits any. Never one that says nobody could reach her or that nothing changed for her.
 */
const ASK_REASONS: readonly SilenceReason[] = [
  'client_requested_the_change',
  'told_by_phone',
  'told_in_person',
];
const toldHerself = (reason: SilenceReason | '') =>
  reason === 'told_by_phone' || reason === 'told_in_person';

/** The answer the office gives a changed address, which she signs in to home with (section 12.4). */
export const SIGN_IN_AGAIN = 'Done. From now on, sign in to home with your new email address.';

/** A field of her payload as she typed it, or nothing. */
function herText(view: HomeRequestView, field: string): string | null {
  const value = view.payload[field];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** The days that suit her, as she chose them. */
function herDays(view: HomeRequestView): readonly string[] {
  const days = view.payload.days_that_suit;
  return Array.isArray(days) ? days.filter((day): day is string => typeof day === 'string') : [];
}

/** An ask the office fetches by id: from a follow-up, which names the ask it was opened for. */
export function HomeRequest({
  id,
  me,
  onDecided,
}: {
  id: string;
  me: Switches;
  onDecided: (sentence: string) => void;
}) {
  const [view, setView] = useState<HomeRequestView | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    setProblem(null);
    getHomeRequest(id)
      .then((answer) => {
        if (live) setView(answer.request);
      })
      .catch((thrown: unknown) => {
        if (live) setProblem(thrown instanceof ApiError ? thrown.message : 'The ask did not load.');
      });
    return () => {
      live = false;
    };
  }, [id, attempt]);

  if (problem) {
    return (
      <ErrorNotice
        title="The ask from home did not load"
        message={problem}
        onRetry={() => setAttempt((n) => n + 1)}
      />
    );
  }
  if (!view) return <Busy what="the ask from home" />;
  return <HomeRequestBody view={view} me={me} onDecided={onDecided} />;
}

/** One ask as the office reads it, with the decision where it is still the office's to make. */
export function HomeRequestBody({
  view,
  me,
  onDecided,
}: {
  view: HomeRequestView;
  me: Switches;
  onDecided: (sentence: string) => void;
}) {
  const doorOpen = view.membership.state === 'granted';
  const decidable = view.state === 'received' && view.waiting === null;
  return (
    <div className="rounded-lg border border-navy/15 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs tracking-wide text-navy/55 uppercase">Asked through home</p>
        <Pill tone={STATE_TONE[view.state]}>{HOME_REQUEST_STATE_LABELS[view.state]}</Pill>
      </div>
      <h3 className="mt-1 font-serif text-lg leading-tight">
        {HOME_REQUEST_KIND_LABELS[view.kind]}
      </h3>
      <p className="text-sm text-navy/75">
        {view.person.displayName}, {formatInstant(view.occurredAt)}
      </p>

      <dl className="mt-2">
        <HerWords view={view} />
      </dl>

      {view.waiting ? (
        <p className="mt-2 text-sm">
          Accepted: moving to {formatDay(view.waiting.toDate)}. Their page at home shows it once
          they have been told, by the message or by the office saying so on the clean.{' '}
          {view.visitId ? (
            <Link to={`/visits/${view.visitId}`} className="underline">
              The clean
            </Link>
          ) : null}
        </p>
      ) : null}

      {view.state !== 'received' ? (
        <dl className="mt-2">
          <Detail label="Answer" value={view.answer} />
          <Detail
            label="Decided"
            value={
              view.decidedAt
                ? `${formatInstant(view.decidedAt)}${view.decidedByName ? `, ${view.decidedByName}` : ''}`
                : null
            }
          />
          <Detail
            label="Why"
            value={view.cancelReason ? HOME_REQUEST_CANCEL_REASON_LABELS[view.cancelReason] : null}
          />
        </dl>
      ) : null}

      {decidable && !doorOpen ? (
        <p className="mt-2 text-sm text-coral">
          {view.person.displayName} no longer has a door onto this household at home, so nothing can
          be decided in their name. If the ask was real, act on it as on a phone call.
        </p>
      ) : null}

      {decidable && doorOpen ? (
        SCHEDULE_ASK_KINDS.has(view.kind) ? (
          <DecideScheduleAsk view={view} me={me} onDecided={onDecided} />
        ) : PEOPLE_ASK_KINDS.has(view.kind) ? (
          <DecidePersonAsk view={view} onDecided={onDecided} />
        ) : (
          <DecideAsk view={view} me={me} onDecided={onDecided} />
        )
      ) : null}
    </div>
  );
}

/** What she asked, in her own words, and what it is about as it stands now. */
function HerWords({ view }: { view: HomeRequestView }) {
  const visit = view.visit;
  const clean = visit
    ? `${formatDay(visit.scheduledDate)}${
        formatWindow(visit.windowStart, visit.windowEnd)
          ? `, ${formatWindow(visit.windowStart, visit.windowEnd)}`
          : ''
      }`
    : null;
  const days = herDays(view)
    .map((day) => formatDay(day))
    .join('; ');
  const proposed = (field: 'do_not_list' | 'pets' | 'entry_notes') =>
    typeof view.payload[field] === 'string' ? String(view.payload[field]) : null;
  // Her own new details are hers, not a person she would add: drawn with what is on file, below.
  const herOwn = view.kind === 'person.contact_change';
  const reach = herOwn
    ? ''
    : [herText(view, 'email'), herText(view, 'phone')].filter(Boolean).join(', ');
  const home = SCHEDULE_ASK_KINDS.has(view.kind) && view.home ? view.home : null;
  return (
    <>
      <Detail label="The clean" value={clean} />
      <Detail
        label="The home"
        value={
          home ? (home.label ? `${home.label}, ${home.addressLine1}` : home.addressLine1) : null
        }
      />
      <Detail label="What they ask" value={askedInWords(view.kind, view.payload)} />
      <Detail label="Their note" value={herText(view, 'note')} />
      <Detail label="Days that suit them" value={days || null} />
      <Detail label="Times that suit them" value={herText(view, 'window')} />
      <Detail label="What they ask for" value={herText(view, 'text')} />
      <Detail label="They wrote" value={herText(view, 'body')} />
      <Detail
        label="Add-on"
        value={
          typeof view.payload.rate_line_code === 'string'
            ? `${view.payload.rate_line_code} (${String(view.payload.quantity ?? 1)})`
            : null
        }
      />
      <Detail
        label="What not to touch"
        value={
          proposed('do_not_list') !== null
            ? `${proposed('do_not_list')}\n(now: ${view.home?.doNotList ?? 'nothing'})`
            : null
        }
      />
      <Detail
        label="Pets"
        value={
          proposed('pets') !== null
            ? `${proposed('pets')}\n(now: ${view.home?.pets ?? 'nothing'})`
            : null
        }
      />
      <Detail
        label="Getting to the door"
        value={
          proposed('entry_notes') !== null
            ? `${proposed('entry_notes')}\n(now: ${view.home?.entryNotes ?? 'nothing'})`
            : null
        }
      />
      <Detail label="The person" value={herOwn ? null : herText(view, 'display_name')} />
      <Detail
        label="On home as"
        value={
          view.kind === 'person.add' && view.payload.role === 'client'
            ? 'A client, who pays and changes the cleans'
            : null
        }
      />
      <Detail label="How to reach them" value={reach || null} />
    </>
  );
}

/** The message she will read, as the office approves it. */
interface Approved {
  readonly on: boolean;
  readonly channel: Channel;
  readonly body: string;
}

/** Why what a decision wrote for her to read will not go, or null when it goes. */
function whyNothingWent(
  went: {
    readonly refusedByConsent: boolean;
    readonly gateRefusal: string | null;
    readonly choiceRefusal?: string | null;
  } | null,
  who: string,
): string | null {
  if (went === null) return null;
  if (went.refusedByConsent) return `${who} has not agreed to be sent it that way`;
  // Their own choice, or the office's hold (ops #76 phase 2), in its words.
  if (went.gateRefusal === null) {
    return went.choiceRefusal ? choiceWords(went.choiceRefusal).replace(/\.$/, '') : null;
  }
  // The switch that held it, named as itself: client email's for an email (ops #76 O6), else texting's.
  return heldByEmailSwitch(went.gateRefusal)
    ? 'client email is not switched on for their address in ops'
    : 'texting is not switched on for their number in ops';
}

/** What the office's sentence says was done, by the schedule ask decided. */
const SCHEDULE_DONE: Partial<Record<HomeRequestView['kind'], string>> = {
  'schedule.pause': 'Paused',
  'schedule.resume': 'Resumed',
  'schedule.change': 'Changed',
  'service.end': 'Ended',
};

/**
 * WHAT DID NOT GO, in words for the office, or null when everything the decision wrote will go: the
 * message that tells her of a move, a skip's confirmation, a reply to her message, or the words beside
 * a schedule decided. The server keeps the follow-up open for each, saying the same.
 */
export function nothingWent(decided: HomeDecided, view: HomeRequestView): string | null {
  const who = view.person.displayName;
  const move = whyNothingWent(decided.change, who);
  if (move !== null) {
    return `Nothing was sent: ${move}. The clean has not moved. Ring ${who} and tell them, then say on the clean that they were told by phone: it moves then, and their page at home shows it.`;
  }
  const message = whyNothingWent(decided.message, who);
  if (message === null) return null;
  const done = SCHEDULE_DONE[view.kind];
  if (done !== undefined) {
    return `${done}, but nothing was sent: ${message}. Their page at home says so; ring ${who} if they should hear it from us.`;
  }
  return view.kind === 'visit.skip'
    ? `The skip is recorded, but nothing was sent: ${message}. Ring ${who} if they should hear it from us.`
    : `Their page at home says it is answered, but nothing was sent: ${message}. Ring ${who}.`;
}

/**
 * THE TWO REFUSALS THAT ARE A DECISION DONE: the clean or the standing schedule the ask was about is
 * gone, so the door cancelled the ask, wrote its `request` push and only then answered 409 with its
 * sentence. Shown as "Not recorded" with Accept still on offer, a second press would meet an ask that is
 * no longer received; so the sentence is handed on as the decision's, and the page reloads the ask.
 */
const CANCELLED_BY_THE_DOOR: ReadonlySet<string> = new Set([
  'schedule_ended',
  'visit_no_longer_open',
]);

/**
 * THE DECISION'S OWN STATE, the same for every form: pressed and waiting, the server's refusal in its
 * own words, and what did not go once it has landed, said before anything else is.
 */
function useDecision(view: HomeRequestView, onDecided: (sentence: string) => void) {
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [held, setHeld] = useState<string | null>(null);

  async function decide(body: HomeDecision, sentence: string) {
    setBusy(true);
    setProblem(null);
    try {
      const decided = await decideHomeRequest(view.id, body);
      const unsent = nothingWent(decided, view);
      if (unsent === null) onDecided(sentence);
      else setHeld(unsent);
    } catch (thrown) {
      if (thrown instanceof ApiError && CANCELLED_BY_THE_DOOR.has(thrown.code)) {
        onDecided(thrown.message);
        return;
      }
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not recorded.');
    } finally {
      setBusy(false);
    }
  }

  return { problem, setProblem, busy, held, decide };
}

/** What did not go, read first, with the clean one tap away where there is one. */
function HeldNotice({
  view,
  held,
  onDecided,
}: {
  view: HomeRequestView;
  held: string;
  onDecided: (sentence: string) => void;
}) {
  return (
    <div className="mt-3 border-t border-navy/10 pt-3" role="alert">
      <p className="mb-2 text-sm text-coral">{held}</p>
      {view.visitId ? (
        <p className="mb-3 text-sm">
          <Link to={`/visits/${view.visitId}`} className="font-medium underline">
            Open the clean
          </Link>
        </p>
      ) : null}
      <Button
        onClick={() =>
          onDecided(`Recorded. Nothing was sent to ${view.person.displayName}, so ring them.`)
        }
      >
        Understood
      </Button>
    </div>
  );
}

/** The office's answer, which she reads on her page at home. */
function AnswerField({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <TextAreaField
      id={id}
      label="Your answer, which they read on their page at home"
      value={value}
      onChange={onChange}
      rows={2}
      hint="Never a code: an answer that reads like one is refused."
    />
  );
}

/** Accept, where it can work, and Decline. */
function DecisionButtons({
  busy,
  onAccept,
  onDecline,
}: {
  busy: boolean;
  onAccept: (() => void) | null;
  onDecline: () => void;
}) {
  return (
    <div className="flex flex-col gap-3 md:flex-row">
      {onAccept ? (
        <Button onClick={onAccept} disabled={busy} full>
          {busy ? 'Recording' : 'Accept'}
        </Button>
      ) : null}
      <Button tone="quiet" onClick={onDecline} disabled={busy}>
        Decline
      </Button>
    </div>
  );
}

/** Her answer's words, or null; a decline needs some, which the office is asked for first. */
const wordsOf = (answer: string) => (answer.trim() === '' ? null : answer.trim());
const NEEDS_A_WORD = 'Say why in a few words they will read on their page at home.';

/**
 * THE DECISION: an answer she reads, and what accepting this kind needs. On a refusal the server's
 * sentence is shown and nothing typed is touched.
 */
function DecideAsk({
  view,
  me,
  onDecided,
}: {
  view: HomeRequestView;
  me: Switches;
  onDecided: (sentence: string) => void;
}) {
  const field = (name: string) => `ask-${view.id}-${name}`;
  const { problem, setProblem, busy, held, decide } = useDecision(view, onDecided);
  const [answer, setAnswer] = useState('');
  // Nothing chosen is left to be decided, as the move flow's own cancel leaves it.
  const [charge, setCharge] = useState<ChargeDecision | ''>('');
  const suits = herDays(view).filter((day) => isDay(day));
  const [toDate, setToDate] = useState(suits[0] ?? '');
  const [message, setMessage] = useState<Approved>({
    on: view.kind === 'visit.move',
    channel: 'email',
    body: '',
  });
  // What the register says of her on each channel, from the preview, before anything is approved.
  const [consent, setConsent] = useState<Readonly<Record<Channel, boolean>> | null>(null);
  const [people, setPeople] = useState<readonly ContactRow[] | null>(null);
  const [contactId, setContactId] = useState('');
  // Her number and address as the household holds them, for the switches' line (ops #76 O6). Unread,
  // she is on no list of test numbers or addresses, the safe direction.
  const [her, setHer] = useState<ContactRow | null>(null);
  const carriesWords =
    view.kind === 'visit.skip' || view.kind === 'visit.move' || view.kind === 'message';

  const messageKind =
    view.kind === 'visit.skip'
      ? 'cancelled_by_client'
      : view.kind === 'visit.move'
        ? 'moved'
        : null;

  // The business's own words for a skip or a move, for the office to edit before it approves them.
  useEffect(() => {
    if (!message.on || messageKind === null || !view.visitId) return;
    if (messageKind === 'moved' && !isDay(toDate)) return;
    let live = true;
    previewMessage(view.visitId, {
      kind: messageKind,
      toDate: messageKind === 'moved' ? toDate : null,
      contactId: view.person.contactId,
      channel: message.channel,
    })
      .then((preview) => {
        if (!live) return;
        setMessage((current) => ({ ...current, body: preview.body }));
        const hers = preview.contacts.find((row) => row.id === view.person.contactId);
        setConsent(hers ? { sms: hers.maySms, email: hers.mayEmail } : null);
      })
      .catch(() => {
        // The office writes the words itself; the box is there either way.
      });
    return () => {
      live = false;
    };
  }, [message.on, message.channel, messageKind, toDate, view.visitId, view.person.contactId]);

  useEffect(() => {
    if (!carriesWords) return;
    let live = true;
    getClient(view.clientId)
      .then((record) => {
        if (live) setHer(record.contacts.find((one) => one.id === view.person.contactId) ?? null);
      })
      .catch(() => {
        // Left unread: the line still says where each switch stands.
      });
    return () => {
      live = false;
    };
  }, [carriesWords, view.clientId, view.person.contactId]);

  // The household's people, for the person she asked to add once the office has added her: each with
  // an email, since the invitation is one, and none already invited to home or on it, whose door an
  // invitation would replace (the server refuses that too, `already_on_home`).
  useEffect(() => {
    if (view.kind !== 'person.add') return;
    let live = true;
    Promise.all([getClient(view.clientId), listMemberships(view.clientId)])
      .then(([record, doors]) => {
        if (!live) return;
        const onHome = new Set(
          doors.memberships
            .filter((door) => door.state !== 'revoked')
            .map((door) => door.contactId),
        );
        setPeople(record.contacts.filter((contact) => contact.email && !onHome.has(contact.id)));
      })
      .catch(() => {
        if (live) setPeople([]);
      });
    return () => {
      live = false;
    };
  }, [view.kind, view.clientId]);

  const words = wordsOf(answer);
  const approved = message.on
    ? { contactId: view.person.contactId, channel: message.channel, body: message.body }
    : null;

  function accept() {
    switch (view.kind) {
      case 'visit.skip':
        return decide(
          {
            outcome: 'accepted',
            answer: words,
            // Sent only when somebody chose it: nothing chosen is written `not_decided`.
            ...(charge ? { chargeDecision: charge } : {}),
            confirmation: approved,
          },
          'Skipped. The clean is off the calendar, and their page at home says so.',
        );
      case 'visit.move':
        if (!isDay(toDate)) return setProblem('Choose the day the clean moves to.');
        return decide(
          { outcome: 'accepted', answer: words, toDate, message: approved },
          'Accepted. The clean moves once they have been told, and their page at home shows it then.',
        );
      case 'person.add':
        if (!contactId) {
          return setProblem('Choose them from the household, once they have been added to it.');
        }
        return decide(
          { outcome: 'accepted', answer: words, contactId },
          'Invited. Home emails them how to sign in.',
        );
      case 'message':
        return decide(
          { outcome: 'accepted', answer: words, reply: approved },
          'Answered, and their page at home says so.',
        );
      default:
        return decide(
          { outcome: 'accepted', answer: words },
          'Accepted, and their page at home says so.',
        );
    }
  }

  function decline() {
    if (words === null) {
      setProblem(NEEDS_A_WORD);
      return;
    }
    void decide({ outcome: 'declined', answer: words }, 'Declined, with your words on their page.');
  }

  const offersMessage = view.kind === 'visit.skip' || view.kind === 'message';
  const messageLabel =
    view.kind === 'visit.skip'
      ? 'Send them a confirmation'
      : view.kind === 'message'
        ? 'Answer them by text or email as well'
        : 'The message that tells them';

  if (held !== null) return <HeldNotice view={view} held={held} onDecided={onDecided} />;

  return (
    <div className="mt-3 border-t border-navy/10 pt-3">
      {problem ? (
        <div className="mb-3">
          <ErrorNotice title="Not recorded" message={problem} />
        </div>
      ) : null}

      <AnswerField id={field('answer')} value={answer} onChange={setAnswer} />

      {view.kind === 'visit.skip' ? (
        <SelectField
          id={field('charge')}
          label="Is the skip charged"
          value={charge}
          onChange={setCharge}
          options={CHARGE_DECISIONS}
          toLabel={(value) => CHARGE_DECISION_LABELS[value]}
          placeholder="Decide later"
          hint="Published policy: no charge with more than a day's notice, no charge the first time under it, and half the rate after that. Left to decide later, the clean's page asks again."
        />
      ) : null}

      {view.kind === 'visit.move' ? (
        <>
          {suits.length > 0 ? (
            <SelectField
              id={field('suits')}
              label="A day that suits them"
              value={suits.includes(toDate) ? toDate : ''}
              onChange={setToDate}
              options={suits}
              toLabel={(day) => formatDay(day) ?? day}
              placeholder="Another day"
            />
          ) : null}
          <TextField
            id={field('day')}
            label="The day it moves to"
            type="date"
            value={toDate}
            onChange={setToDate}
          />
        </>
      ) : null}

      {view.kind === 'person.add' ? (
        <SelectField
          id={field('person')}
          label="Who they are on the household"
          value={contactId}
          onChange={setContactId}
          options={(people ?? []).map((person) => person.id)}
          toLabel={(id) => people?.find((person) => person.id === id)?.displayName ?? id}
          placeholder={people === null ? 'Loading the household' : 'Choose them'}
          hint={`Add them to the household first, with what they agree to be told on. Accepting invites them to home as ${view.payload.role === 'client' ? 'a client' : 'a member'}.`}
        />
      ) : null}

      {offersMessage ? (
        <label className="mb-3 flex min-h-11 desk:min-h-9 items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={message.on}
            onChange={(event) =>
              setMessage((current) => ({ ...current, on: event.target.checked }))
            }
          />
          {messageLabel}
        </label>
      ) : null}

      {message.on && (offersMessage || view.kind === 'visit.move') ? (
        <>
          <SelectField
            id={field('channel')}
            label={`How ${view.person.displayName} is told`}
            value={message.channel}
            onChange={(channel) => setMessage((current) => ({ ...current, channel }))}
            options={CHANNELS}
            toLabel={(channel) => CHANNEL_WORDS[channel]}
            hint="Their consent decides: a message the register refuses is written down and never sent."
          />
          <SwitchLine
            channel={message.channel}
            me={me}
            her={her}
            wayOn={
              view.kind === 'visit.move'
                ? 'Send it the other way if that would go, or ring them and move the clean on its own page, saying they were told by phone.'
                : 'Send it the other way if that would go, or ring them.'
            }
          />
          {/* What their choices would do to a change's message (ops #76 phase 2); a reply reads none. */}
          {view.kind !== 'message' &&
          choiceLineOf(her?.messaging?.choices?.visit_changes?.[message.channel]) !== null ? (
            <p id={field('choice')} className="mb-3 text-sm text-coral wrap-anywhere">
              {choiceLineOf(her?.messaging?.choices?.visit_changes?.[message.channel])}
            </p>
          ) : null}
          {consent !== null && !consent[message.channel] ? (
            <p className="mb-3 text-sm text-coral">
              {view.person.displayName} has not agreed to{' '}
              {message.channel === 'sms' ? 'texts' : 'emails'} about their cleans, so this would be
              written down and never sent.{' '}
              {view.kind === 'visit.move'
                ? 'Send it the other way if they agreed to that, or ring them and move the clean on its own page, saying they were told by phone.'
                : 'Send it the other way if they agreed to that, or ring them.'}
            </p>
          ) : null}
          <TextAreaField
            id={field('body')}
            label="The words they read"
            value={message.body}
            onChange={(body) => setMessage((current) => ({ ...current, body }))}
            rows={4}
          />
        </>
      ) : null}

      {view.kind === 'visit.addon' ? (
        <p className="mb-3 text-sm text-navy/75">
          Ops has no add-on door yet, so an add-on cannot be accepted here. Ring them, agree it,
          then decline this with a word saying what you agreed.
        </p>
      ) : null}

      <DecisionButtons
        busy={busy}
        onAccept={view.kind !== 'visit.addon' ? () => void accept() : null}
        onDecline={decline}
      />
      {view.visit ? (
        <p className="mt-2 text-xs text-navy/65">
          Received {formatDay(dayOf(view.receivedAt))}.{' '}
          <Link to={`/visits/${view.visit.id}`} className="underline">
            Open the clean
          </Link>
        </p>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Her standing cleans: a pause, a resume, a change, an ending.
 * ------------------------------------------------------------------ */

/**
 * A SCHEDULE ASK, DECIDED (section 12.4): the home's standing schedule read first, then the form for
 * what she asked, against it. Nothing is offered as decided until the schedule has been read, since
 * every default and every draft is read off it.
 *
 * A READ THAT FAILED IS NOT A SCHEDULE THAT IS GONE (the review's C11-2). Taken as none, the form said
 * Accept cancels her ask, and sent a bare acceptance the door read against her real schedule: a resume
 * as she asked needs no reason and no words, so her cleans came back while the office was told her ask
 * was cancelled. So a failed read says so, with a retry, and offers no decision; only a home the ask
 * names none of, or an answer that came back empty, is the schedule gone.
 */
function DecideScheduleAsk({
  view,
  me,
  onDecided,
}: {
  view: HomeRequestView;
  me: Switches;
  onDecided: (sentence: string) => void;
}) {
  const [list, setList] = useState<AgreementList | null>(null);
  const [unread, setUnread] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [record, setRecord] = useState<ClientRecord | null>(null);
  const [lines, setLines] = useState<readonly RateLine[] | null>(null);

  useEffect(() => {
    let live = true;
    setUnread(null);
    if (view.siteId === null) {
      setList({ agreements: [], series: [] });
      return;
    }
    listAgreements(view.siteId)
      .then((answer) => {
        if (live) setList(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setList(null);
        setUnread(thrown instanceof ApiError ? thrown.message : 'The read failed.');
      });
    return () => {
      live = false;
    };
  }, [view.siteId, attempt]);

  // Her register and the home's shape: the consent line and a new cadence's price, read beside it.
  useEffect(() => {
    let live = true;
    getClient(view.clientId)
      .then((answer) => {
        if (live) setRecord(answer);
      })
      .catch(() => {
        // The form stands without it: no consent line drawn, and a new cadence priced on the server.
      });
    return () => {
      live = false;
    };
  }, [view.clientId]);

  useEffect(() => {
    if (view.kind !== 'schedule.change') return;
    let live = true;
    getCatalog()
      .then((answer) => {
        if (live) setLines(answer.lines);
      })
      .catch(() => {
        // Without the card the words name no new price, and the door says so if they must.
      });
    return () => {
      live = false;
    };
  }, [view.kind]);

  if (unread !== null) {
    return (
      <div className="mt-3 border-t border-navy/10 pt-3">
        <ErrorNotice
          title="Their standing schedule did not load"
          message={`${unread} Nothing can be decided on it until it is read.`}
          onRetry={() => setAttempt((n) => n + 1)}
        />
      </div>
    );
  }
  if (list === null) return <Busy what="their standing schedule" />;
  const standing = standingOf(list, serviceOfAsk(view.kind, view.payload));
  return (
    <ScheduleDecision
      view={view}
      me={me}
      standing={standing}
      record={record}
      lines={lines}
      onDecided={onDecided}
    />
  );
}

/** A time of day, as the agreement's own forms take it. */
function TimeInput({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Field id={id} label={label}>
      <input
        id={id}
        type="time"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 block min-h-11 desk:min-h-9 w-full rounded-lg border border-navy/25 bg-parchment px-3 py-2 text-navy"
      />
    </Field>
  );
}

/** The words she will read beside a schedule decision, drafted until the office edits them. */
interface Words {
  readonly on: boolean;
  readonly channel: Channel;
  /** What the office typed over the draft, or null while the draft stands. */
  readonly edited: string | null;
}

/** Her register, by channel, or null where the household read did not carry it. */
function mayOn(messaging: ContactMessaging | null | undefined, channel: Channel): boolean | null {
  if (!messaging) return null;
  return channel === 'sms' ? messaging.maySms : messaging.mayEmail;
}

function ScheduleDecision({
  view,
  me,
  standing,
  record,
  lines,
  onDecided,
}: {
  view: HomeRequestView;
  me: Switches;
  standing: Standing | null;
  record: ClientRecord | null;
  lines: readonly RateLine[] | null;
  onDecided: (sentence: string) => void;
}) {
  const field = (name: string) => `ask-${view.id}-${name}`;
  const { problem, setProblem, busy, held, decide } = useDecision(view, onDecided);
  const kind = view.kind;
  const payload = view.payload;
  const series = standing?.series ?? null;
  const asked = kind === 'schedule.change' && standing ? changeAsked(payload, standing) : null;
  const who = view.person.displayName;

  const [answer, setAnswer] = useState('');
  const [reason, setReason] = useState<SilenceReason | ''>('');
  // Every day starts as the one she named.
  const [first, setFirst] = useState(
    payloadText(payload, kind === 'service.end' ? 'last_day' : 'from') ?? '',
  );
  const [until, setUntil] = useState(payloadText(payload, 'until') ?? '');
  const [holdSlot, setHoldSlot] = useState('');
  const [cadence, setCadence] = useState<Cadence | ''>(
    asked?.cadence ?? standing?.agreement.cadence ?? '',
  );
  const [anchor, setAnchor] = useState(asked?.moves ? asked.anchor : '');
  const [start, setStart] = useState(series?.windowStartLocal ?? '');
  const [finish, setFinish] = useState(series?.windowEndLocal ?? '');
  const [quoted, setQuoted] = useState('');
  const [words, setWords] = useState<Words>({
    on: kind === 'schedule.change',
    channel: 'email',
    edited: null,
  });

  if (held !== null) return <HeldNotice view={view} held={held} onDecided={onDecided} />;

  const service = serviceOfAsk(kind, payload);
  const cadenceNow = standing?.agreement.cadence ?? null;
  const newCadence = cadence !== '' && cadence !== cadenceNow;
  const hoursOnly = kind === 'schedule.change' && !newCadence && anchor === '';
  const hoursMoved =
    series !== null && (start !== series.windowStartLocal || finish !== series.windowEndLocal);

  // A NEW CADENCE'S PRICE, read off the card for the home's shape, or the figure the office quoted her
  // after a walkthrough where the card quotes that way. Any other change keeps the agreement's own.
  const site = record?.sites.find((one) => one.id === view.siteId) ?? null;
  const line =
    newCadence && lines !== null && site !== null
      ? houseLineForHome(lines, site.bedrooms, site.bathrooms, cadence)
      : null;
  const walkthrough = line !== null && line.priceCents === null;
  const price = !newCadence
    ? (standing?.agreement.priceCents ?? null)
    : walkthrough
      ? centsFromDollars(quoted)
      : (line?.priceCents ?? null);

  // The first clean under the decision, and its weekday, for the words that tell her.
  const firstClean =
    asked === null || series === null
      ? null
      : hoursOnly
        ? firstUnwritten(series)
        : anchor !== ''
          ? anchor
          : isDay(first)
            ? firstOnOrAfter(first, asked.weekday)
            : null;

  const firstName = firstNameOf(who);
  const draft =
    kind === 'schedule.change'
      ? firstClean !== null && isDay(firstClean) && cadence !== ''
        ? draftChange({
            firstName,
            firstClean,
            cadence,
            weekday: weekdayWordOf(hoursOnly && series ? series.anchorDate : firstClean),
            startLocal: start,
            endLocal: finish,
            priceCents: price,
            hoursOnly,
            channel: words.channel,
          })
        : ''
      : isDay(first)
        ? draftConfirmation(kind, {
            firstName,
            from: first,
            until: kind === 'schedule.pause' && isDay(until) ? until : null,
            service,
            channel: words.channel,
          })
        : '';
  const body = words.edited ?? draft;

  // WHETHER THE DECISION IS WHAT SHE ASKED, as the door reads it: only then may it carry her own
  // request as its reason, and otherwise it is a call's.
  const asAsked =
    kind === 'schedule.pause'
      ? first === payloadText(payload, 'from') &&
        (until === '' ? null : until) === payloadText(payload, 'until')
      : kind === 'schedule.resume'
        ? first === payloadText(payload, 'from')
        : kind === 'service.end'
          ? first === payloadText(payload, 'last_day')
          : asked !== null && standing !== null && cadence !== ''
            ? changeAsAsked(asked, standing, { fromDate: first, cadence, anchorDate: anchor })
            : true;

  const hers = record?.contacts.find((one) => one.id === view.person.contactId);
  const may = mayOn(hers?.messaging, words.channel);
  const approved = words.on
    ? { contactId: view.person.contactId, channel: words.channel, body }
    : null;
  const answerWords = wordsOf(answer);
  const reasonOrNull = reason === '' ? null : reason;
  const nothingStands = standing === null || (kind !== 'service.end' && series === null);

  function accept() {
    const done = SCHEDULE_DONE[kind] ?? 'Accepted';
    // Nothing to decide it on: the door cancels her ask `schedule_ended` and says so in its words.
    if (nothingStands) {
      return decide(
        { outcome: 'accepted', answer: answerWords },
        'Their ask is cancelled, and their page at home says so.',
      );
    }
    if (!isDay(first)) {
      return setProblem(
        kind === 'service.end'
          ? 'Choose their last day.'
          : kind === 'schedule.change'
            ? 'Choose the day the change takes over from.'
            : 'Choose the day it starts.',
      );
    }
    if (words.on && body.trim() === '') {
      return setProblem('Write the words they will read, or untick them.');
    }
    switch (kind) {
      case 'schedule.pause':
        if (reason === '') return setProblem(REASON_NEEDED);
        if (until !== '' && (!isDay(until) || until < first)) {
          return setProblem('A pause cannot end before it begins.');
        }
        return decide(
          {
            outcome: 'accepted',
            answer: answerWords,
            pausedFrom: first,
            pausedUntil: until === '' ? null : until,
            holdSlotUntil: isDay(holdSlot) ? holdSlot : null,
            silenceReason: reasonOrNull,
            confirmation: approved,
          },
          `${done}. Their cleans in the pause are off the calendar, each with its record, and their page at home says so.`,
        );
      case 'schedule.resume':
        return decide(
          {
            outcome: 'accepted',
            answer: answerWords,
            resumeOn: first,
            silenceReason: reasonOrNull,
            confirmation: approved,
          },
          `${done}. Their cleans are written back in, and their page at home says so.`,
        );
      case 'service.end':
        if (reason === '') return setProblem(REASON_NEEDED);
        return decide(
          {
            outcome: 'accepted',
            answer: answerWords,
            endedOn: first,
            silenceReason: reasonOrNull,
            confirmation: approved,
          },
          `${done}. Every clean after it is off the calendar, each with its record, and their page at home says so.`,
        );
      default: {
        if (cadence === '') return setProblem('Choose the cadence.');
        if (!hoursOnly && reason === '') return setProblem(REASON_NEEDED);
        if (!words.on && !toldHerself(reason)) {
          return setProblem(
            'A change to their standing cleans is news to them: the day, the hours and, on a new cadence, the price. Approve the words they will read, or ring them first and choose that you told them by phone.',
          );
        }
        if (!isTimeOfDay(start) || !isTimeOfDay(finish) || start >= finish) {
          return setProblem('The arrival window has to end after it starts.');
        }
        if (walkthrough && centsFromDollars(quoted) === null) {
          return setProblem('Type the figure you quoted them after the walkthrough, in dollars.');
        }
        return decide(
          {
            outcome: 'accepted',
            answer: answerWords,
            fromDate: first,
            cadence,
            anchorDate: anchor === '' ? null : anchor,
            ...(hoursMoved ? { windowStartLocal: start, windowEndLocal: finish } : {}),
            ...(walkthrough ? { quotedPriceCents: centsFromDollars(quoted) } : {}),
            silenceReason: reasonOrNull,
            message: approved,
          },
          `${done}. Their standing cleans are as you decided, and their page at home says so.`,
        );
      }
    }
  }

  function decline() {
    if (answerWords === null) {
      setProblem(NEEDS_A_WORD);
      return;
    }
    void decide(
      { outcome: 'declined', answer: answerWords },
      'Declined, with your words on their page.',
    );
  }

  const wordsLabel =
    kind === 'schedule.change' ? 'Send them the words that tell them' : 'Send them a confirmation';

  return (
    <div className="mt-3 border-t border-navy/10 pt-3">
      {problem ? (
        <div className="mb-3">
          <ErrorNotice title="Not recorded" message={problem} />
        </div>
      ) : null}

      <dl className="mb-3">
        <Detail label="What they have now" value={standing ? standingInWords(standing) : null} />
      </dl>
      {nothingStands ? (
        <p className="mb-3 text-sm text-coral">
          This home has no standing schedule of that service now, so there is nothing to decide it
          on. Accepting cancels their ask, and their page at home says so; or decline it with a
          word.
        </p>
      ) : null}

      <AnswerField id={field('answer')} value={answer} onChange={setAnswer} />

      {kind === 'schedule.pause' ? (
        <>
          <TextField
            id={field('from')}
            label="Paused from"
            type="date"
            required
            value={first}
            onChange={setFirst}
          />
          <TextField
            id={field('until')}
            label="Paused until"
            type="date"
            value={until}
            onChange={setUntil}
            hint="Empty for no return date yet, as they may have asked."
          />
          <TextField
            id={field('hold')}
            label="Hold their slot until"
            type="date"
            value={holdSlot}
            onChange={setHoldSlot}
            hint={holdSlotHint(holdSlot)}
          />
        </>
      ) : null}

      {kind === 'schedule.resume' ? (
        <TextField
          id={field('from')}
          label="Cleans resume on"
          type="date"
          required
          value={first}
          onChange={setFirst}
          hint="Later than their pause was set to end, the cleans in between come off the calendar, each with a record carrying the reason below."
        />
      ) : null}

      {kind === 'service.end' ? (
        <TextField
          id={field('from')}
          label="Their last day"
          type="date"
          required
          value={first}
          onChange={setFirst}
          hint="Every clean after it comes off the calendar, each with its record. The household itself is not ended by it."
        />
      ) : null}

      {kind === 'schedule.change' ? (
        <>
          <TextField
            id={field('from')}
            label="The change takes over from"
            type="date"
            required
            value={first}
            onChange={setFirst}
            hint="Every clean from this day on comes off the old schedule, each with its record."
          />
          <SelectField
            id={field('cadence')}
            label="How often"
            value={cadence}
            onChange={setCadence}
            options={CADENCES.filter((value) => value !== 'one_time')}
            toLabel={(value) => CADENCE_LABELS[value]}
            hint={
              newCadence
                ? 'Cadence sets the price: this ends their agreement the day before and opens one priced from the card.'
                : undefined
            }
          />
          <TextField
            id={field('anchor')}
            label="First clean on the new day"
            type="date"
            value={anchor}
            onChange={setAnchor}
            hint={
              isDay(anchor)
                ? `Every clean after it is a ${weekdayWordOf(anchor).replace(/^./, (c) => c.toUpperCase())}.`
                : 'Empty to keep the day they have, changing only the hours.'
            }
          />
          <div className="grid grid-cols-2 gap-3">
            <TimeInput id={field('start')} label="Arrives from" value={start} onChange={setStart} />
            <TimeInput
              id={field('finish')}
              label="Arrives by"
              value={finish}
              onChange={setFinish}
            />
          </div>
          {walkthrough ? (
            <TextField
              id={field('quoted')}
              label="The figure you quoted them after the walkthrough, in dollars"
              value={quoted}
              onChange={setQuoted}
              inputMode="numeric"
              hint="The card quotes this home after a walkthrough, so it publishes no figure for the new cadence."
            />
          ) : null}
          {newCadence && series?.pausedFrom ? (
            <p className="mb-3 text-sm text-coral">
              Their cleans are paused, and a new cadence opens a schedule with no pause, which would
              write cleans while they are away. Resume them first, from the day they are back.
            </p>
          ) : null}
        </>
      ) : null}

      <SelectField
        id={field('reason')}
        label="How they know"
        value={reason}
        onChange={setReason}
        options={ASK_REASONS}
        toLabel={(value) => SILENCE_REASON_LABELS[value]}
        placeholder="Choose one"
        hint="Every clean taken off their calendar gets a record with this reason on it. They asked for it themselves only when it is exactly what they asked."
      />
      {!asAsked && !toldHerself(reason) ? (
        <p className="mb-3 text-sm text-coral">
          That is not what they asked: {lowerFirst(askedInWords(kind, payload) ?? '')} Ring {who}{' '}
          and choose that you told them by phone or in person, or decline it with a word so they can
          ask again.
        </p>
      ) : null}

      <label className="mb-3 flex min-h-11 desk:min-h-9 items-center gap-2 text-sm">
        <input
          id={field('send')}
          type="checkbox"
          checked={words.on}
          onChange={(event) => setWords((current) => ({ ...current, on: event.target.checked }))}
        />
        {wordsLabel}
      </label>
      {kind === 'schedule.change' && !words.on ? (
        <p className="mb-3 text-xs text-navy/65">
          A change to their standing cleans is news to them, so it goes with the words they read, or
          after a call with them.
        </p>
      ) : null}

      {words.on ? (
        <>
          <SelectField
            id={field('channel')}
            label={`How ${who} is told`}
            value={words.channel}
            onChange={(channel) =>
              setWords((current) => ({
                ...current,
                channel,
                // The draft is redrafted for the channel; words the office edited keep the opt-out
                // line a text ends with, or lose it on an email (the review's C11-4).
                edited: current.edited === null ? null : wordsOnChannel(current.edited, channel),
              }))
            }
            options={CHANNELS}
            toLabel={(channel) => CHANNEL_WORDS[channel]}
            hint="Their consent decides: words the register refuses are written down and never sent."
          />
          <SwitchLine
            channel={words.channel}
            me={me}
            her={hers}
            wayOn={
              kind === 'schedule.change'
                ? 'Nothing changes until it can: send it the other way if that would go, or ring them and choose that you told them by phone.'
                : 'Send it the other way if that would go, or ring them.'
            }
          />
          {/* What their choices would do to the words beside it (ops #76 phase 2). */}
          {choiceLineOf(hers?.messaging?.choices?.visit_changes?.[words.channel]) !== null ? (
            <p id={field('choice')} className="mb-3 text-sm text-coral wrap-anywhere">
              {choiceLineOf(hers?.messaging?.choices?.visit_changes?.[words.channel])}
            </p>
          ) : null}
          {may === false ? (
            <p className="mb-3 text-sm text-coral">
              {who} has not agreed to {words.channel === 'sms' ? 'texts' : 'emails'} about their
              cleans, so this would not go.{' '}
              {kind === 'schedule.change'
                ? 'Nothing changes until it can: send it the other way if they agreed to that, or ring them and choose that you told them by phone.'
                : 'Send it the other way if they agreed to that, or ring them.'}
            </p>
          ) : null}
          <TextAreaField
            id={field('body')}
            label="The words they read"
            value={body}
            onChange={(typed) => setWords((current) => ({ ...current, edited: typed }))}
            rows={4}
            hint={
              kind === 'schedule.change'
                ? 'Drafted from what you decided: the first clean, the day, the hours and their price of one clean. Their price is the one figure the words may name.'
                : undefined
            }
          />
        </>
      ) : null}

      <DecisionButtons busy={busy} onAccept={() => void accept()} onDecline={decline} />
    </div>
  );
}

const REASON_NEEDED =
  'Say how they know the cleans coming off their calendar are off: as they asked, or told by phone or in person.';

/**
 * WHERE THE DAY HER SLOT IS HELD TO IS READ (the review's C11-3): in ops alone. It reaches `pauseSeries`
 * and no push, so her page at home never shows it; the answer she does read is where she is told how
 * long her slot is held (the design's section 3.5), and the hint says so, naming the day once typed.
 */
function holdSlotHint(holdSlot: string): string {
  const kept =
    'How long before their slot can honestly be offered to somebody else. Kept in ops, and not shown to them at home:';
  return isDay(holdSlot)
    ? `${kept} say in your answer above that their slot is held until ${longDay(holdSlot)}.`
    : `${kept} say in your answer above how long their slot is held.`;
}

/** "a pause from Monday, September 21", for a sentence that names her ask after a colon. */
const lowerFirst = (words: string) => words.replace(/^./, (first) => first.toLowerCase());

/* ------------------------------------------------------------------ *
 * A person on the household: her own details, removed, her homes changed, or taken off home.
 * ------------------------------------------------------------------ */

/**
 * AN ASK ABOUT A PERSON, DECIDED (section 12.4): the household and its doors read first, so the person
 * she named is named here and never an id, and her own new details are drawn beside the ones on file.
 * Accepting writes it through the office's own door onto a person, whose refusals come back in its
 * words: the primary (made another first, on her card), the last person, a number another holds.
 */
function DecidePersonAsk({
  view,
  onDecided,
}: {
  view: HomeRequestView;
  onDecided: (sentence: string) => void;
}) {
  const field = (name: string) => `ask-${view.id}-${name}`;
  const { problem, setProblem, busy, held, decide } = useDecision(view, onDecided);
  const kind = view.kind;
  const payload = view.payload;
  const [record, setRecord] = useState<ClientRecord | null>(null);
  const [doors, setDoors] = useState<readonly MembershipRow[] | null>(null);
  const [answer, setAnswer] = useState('');
  // A CHANGED ADDRESS IS THE ONE SHE SIGNS IN TO HOME WITH, so an Accept the office wrote nothing for
  // answers her with that. Put in her answer only when Accept is pressed, never into the box: there it
  // got a Decline past the ask for a word, and she read "Done" on a declined ask (the review's C11-1).
  const signsInAgain = kind === 'person.contact_change' && payloadText(payload, 'email') !== null;

  useEffect(() => {
    let live = true;
    Promise.all([getClient(view.clientId), listMemberships(view.clientId)])
      .then(([household, answer]) => {
        if (!live) return;
        setRecord(household);
        setDoors(answer.memberships);
      })
      .catch(() => {
        if (!live) return;
        setRecord(null);
        setDoors([]);
      });
    return () => {
      live = false;
    };
  }, [view.clientId]);

  if (held !== null) return <HeldNotice view={view} held={held} onDecided={onDecided} />;
  if (doors === null) return <Busy what="the household" />;

  const who = view.person.displayName;
  const named = payloadText(payload, 'person_id');
  const person =
    record?.contacts.find((one) => one.id === (named ?? view.person.contactId)) ?? null;
  const door =
    person === null
      ? null
      : (doors.find((one) => one.contactId === person.id && one.state !== 'revoked') ?? null);
  const homes = Array.isArray(payload.homes)
    ? (payload.homes as unknown[])
        .filter((id): id is string => typeof id === 'string')
        .map((id) => {
          const home = record?.sites.find((one) => one.id === id);
          return home ? (home.label ?? home.addressLine1) : 'A home no longer on file';
        })
        .join('; ')
    : 'Every home of the household';
  const answerWords = wordsOf(answer);

  const done: Partial<Record<HomeRequestView['kind'], string>> = {
    'person.contact_change': 'Written onto their contact, and their page at home says so.',
    'person.remove': `Removed ${person?.displayName ?? 'the person'} from the household, and their page at home says so.`,
    'person.change': `Invited ${person?.displayName ?? 'them'} again with those homes. Home emails them how to sign in.`,
    'person.leave': `Taken ${who} off home at their own request.`,
  };

  function accept() {
    void decide(
      { outcome: 'accepted', answer: answerWords ?? (signsInAgain ? SIGN_IN_AGAIN : null) },
      done[kind] ?? 'Accepted, and their page at home says so.',
    );
  }

  function decline() {
    // The words that say her change was done are never the reason it was not.
    if (answerWords === null || answerWords === SIGN_IN_AGAIN) {
      setProblem(NEEDS_A_WORD);
      return;
    }
    void decide(
      { outcome: 'declined', answer: answerWords },
      'Declined, with your words on their page.',
    );
  }

  const beside = (asked: string | null, now: string | null) =>
    asked === null ? null : `${asked}\n(now: ${now ?? 'nothing'})`;

  return (
    <div className="mt-3 border-t border-navy/10 pt-3">
      {problem ? (
        <div className="mb-3">
          <ErrorNotice title="Not recorded" message={problem} />
        </div>
      ) : null}

      {kind === 'person.contact_change' ? (
        <>
          <dl className="mb-3">
            <Detail
              label="Name"
              value={beside(payloadText(payload, 'display_name'), person?.displayName ?? null)}
            />
            <Detail
              label="Email"
              value={beside(payloadText(payload, 'email'), person?.email ?? null)}
            />
            <Detail
              label="Number"
              value={beside(
                formatPhone(payloadText(payload, 'phone')),
                formatPhone(person?.phoneE164 ?? null),
              )}
            />
          </dl>
          {payloadText(payload, 'phone') !== null ? (
            <p className="mb-3 text-xs text-navy/65">
              A new number holds no agreement to texts until they give it again for that number.
            </p>
          ) : null}
        </>
      ) : null}

      {kind === 'person.remove' || kind === 'person.change' ? (
        <dl className="mb-3">
          <Detail
            label="The person"
            value={
              person
                ? `${person.displayName}, ${humanise(person.role).toLowerCase()}`
                : 'Nobody on the household now'
            }
          />
          <Detail
            label="The homes they would see"
            value={kind === 'person.change' ? homes : null}
          />
        </dl>
      ) : null}

      {kind === 'person.remove' && person?.role === 'primary' ? (
        <p className="mb-3 text-sm text-coral">
          {person.displayName} is the household&apos;s primary contact. Make another person primary
          on their card first, then accept this.
        </p>
      ) : null}
      {kind === 'person.change' && person !== null && door === null ? (
        <p className="mb-3 text-sm text-coral">
          {person.displayName} has no door onto home to change. Decline this with a word; to give
          them one, invite them from their card.
        </p>
      ) : null}
      {(kind === 'person.remove' || kind === 'person.change') && person === null ? (
        <p className="mb-3 text-sm text-coral">
          The person {who} named is no longer on the household. Decline this with a word saying so.
        </p>
      ) : null}
      {kind === 'person.leave' ? (
        <p className="mb-3 text-sm">
          Accepting takes {who} off home at their own request: their door ends, and they see nothing
          of the household there after. They stay on the household here.
        </p>
      ) : null}

      <AnswerField id={field('answer')} value={answer} onChange={setAnswer} />
      {signsInAgain ? (
        <p className="mb-3 text-xs text-navy/65">
          {`Left empty, Accept answers them: "${SIGN_IN_AGAIN}" Anything you write instead should tell them to sign in to home with the new address.`}
        </p>
      ) : null}
      <DecisionButtons busy={busy} onAccept={accept} onDecline={decline} />
    </div>
  );
}
